<template>
  <v-navigation-drawer
    :value="value"
    fixed
    right
    temporary
    :hide-overlay="activeSnapshotId != null"
    width="480"
    class="zh"
    @input="$emit('input', $event)"
  >
    <div class="zh-head">
      <h3>История изменений</h3>
      <v-btn icon small @click="$emit('input', false)">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </div>

    <div class="zh-filters">
      <v-btn-toggle v-model="source" class="zh-toggle">
        <v-btn value="" depressed>
          Все
        </v-btn>
        <v-btn
          v-for="(name, key) in sources"
          :key="key"
          :value="key"
          depressed
        >
          {{ name }}
        </v-btn>
      </v-btn-toggle>
      <v-switch
        v-if="currentFilial !== 'all'"
        v-model="wholeNetwork"
        label="Вся сеть"
        hide-details
        dense
        inset
        class="zh-switch"
      />
    </div>
    <div v-if="currentFilial !== 'all' && !wholeNetwork" class="zh-scope">
      {{ filialName(currentFilial) }}
    </div>

    <v-progress-linear v-if="loading && !items.length" indeterminate color="primary" />

    <div class="zh-list">
      <div
        v-for="item in items"
        :key="item.id"
        class="zh-item"
        :class="{ active: isActive(item) }"
      >
        <div class="zh-meta">
          <span>{{ date(item.createdAt) }}</span>
          <span class="zh-user">{{ item.user ? item.user.name : 'Система' }}</span>
          <span class="zh-src" :class="item.source">{{ sources[item.source] || item.source }}</span>
        </div>
        <div class="zh-title" :title="text(item).title">
          {{ text(item).title }}
        </div>
        <div
          v-for="(line, i) in text(item).lines"
          :key="i"
          class="zh-line"
          :title="line"
        >
          {{ line }}
        </div>
        <div v-if="currentFilial === 'all' || wholeNetwork" class="zh-filial">
          {{ item.filialId ? filialName(item.filialId) : '' }}
        </div>

        <div v-if="hasVersionButton(item) || canRollbackImport(item)" class="zh-actions">
          <v-tooltip v-if="hasVersionButton(item)" bottom>
            <template #activator="{ on }">
              <span v-on="on">
                <v-btn
                  text
                  x-small
                  class="zh-version"
                  :color="isActive(item) ? 'primary' : undefined"
                  :disabled="!versionSnapshotId(item)"
                  @click="preview(item)"
                >
                  <v-icon x-small left>
                    mdi-backup-restore
                  </v-icon>
                  {{ isActive(item) ? 'Показана на карте' : 'Версия до изменения' }}
                </v-btn>
              </span>
            </template>
            <span>{{ versionSnapshotId(item) ? 'Показать зоны филиала, какими они были до этого изменения' : 'Снимка нет: изменение раньше включения версий' }}</span>
          </v-tooltip>
          <v-btn
            v-if="isActive(item)"
            text
            x-small
            class="zh-version zh-exit"
            @click="$emit('exit-preview')"
          >
            <v-icon x-small left>
              mdi-close
            </v-icon>
            Выйти из просмотра
          </v-btn>
          <v-btn
            v-if="canRollbackImport(item)"
            text
            x-small
            color="primary"
            class="zh-version"
            @click="$emit('restore-import', item)"
          >
            <v-icon x-small left>
              mdi-undo-variant
            </v-icon>
            Откатить импорт
          </v-btn>
        </div>

        <template v-if="item.event === 'import'">
          <button type="button" class="zh-more-link" @click="toggleBatch(item)">
            {{ batches[item.importId] && batches[item.importId].open ? 'Скрыть зоны' : 'Показать зоны' }}
          </button>
          <div v-if="batches[item.importId] && batches[item.importId].open" class="zh-batch">
            <v-progress-linear v-if="batches[item.importId].loading" indeterminate color="primary" />
            <div
              v-for="child in batches[item.importId].items"
              :key="child.id"
              class="zh-child"
              :class="{ active: isActive(child) }"
            >
              <div class="zh-title small" :title="text(child).title">
                {{ text(child).title }}
              </div>
              <div
                v-for="(line, i) in text(child).lines"
                :key="i"
                class="zh-line"
                :title="line"
              >
                {{ line }}
              </div>
              <v-btn
                v-if="versionSnapshotId(child)"
                text
                x-small
                class="zh-version"
                :color="isActive(child) ? 'primary' : undefined"
                @click="preview(child)"
              >
                <v-icon x-small left>
                  mdi-backup-restore
                </v-icon>
                {{ isActive(child) ? 'Показана на карте' : 'Версия филиала до импорта' }}
              </v-btn>
              <v-btn
                v-if="isActive(child)"
                text
                x-small
                class="zh-version zh-exit"
                @click="$emit('exit-preview')"
              >
                <v-icon x-small left>
                  mdi-close
                </v-icon>
                Выйти из просмотра
              </v-btn>
            </div>
            <v-btn
              v-if="batches[item.importId].hasMore"
              text
              small
              color="primary"
              :loading="batches[item.importId].loading"
              @click="loadBatch(item.importId)"
            >
              Ещё
            </v-btn>
          </div>
        </template>
      </div>

      <div v-if="!loading && !items.length" class="zh-empty">
        Изменений пока нет
      </div>

      <v-btn
        v-if="hasMore"
        block
        depressed
        outlined
        class="zh-more"
        :loading="loading"
        @click="load()"
      >
        Показать ещё
      </v-btn>
    </div>
  </v-navigation-drawer>
</template>

<script>
  import { mapActions } from 'vuex'
  import { SOURCE_NAMES, describeHistory, historyDate } from './zonesHistoryText'

  /**
   * Журнал изменений зон доставки: ручные правки, тарифы, импорты (с раскрытием до зон), крон Юпитера.
   */
  export default {
    name: 'ZonesHistoryPanel',
    props: {
      value: { type: Boolean, default: false },
      filials: { type: Array, default: () => [] },
      currentFilial: { type: [String, Number], default: 'all' },
      // снимок, который сейчас показан на карте (режим просмотра версии)
      activeSnapshotId: { type: Number, default: null },
      canEdit: { type: Boolean, default: false },
      // счётчик от страницы: изменился - перечитать список (после отката)
      refresh: { type: Number, default: 0 },
    },
    data: () => ({
      sources: SOURCE_NAMES,
      source: '',
      wholeNetwork: false,
      items: [],
      page: 0,
      hasMore: false,
      loading: false,
      batches: {},
    }),
    watch: {
      value (open) {
        if (open) this.reset()
      },
      source () {
        this.reset()
      },
      wholeNetwork () {
        this.reset()
      },
      refresh () {
        if (this.value) this.reset()
      },
    },
    methods: {
      ...mapActions('polygons', ['fetchHistory']),
      reset () {
        this.items = []
        this.page = 0
        this.hasMore = false
        this.batches = {}
        this.load()
      },
      async load () {
        this.loading = true
        try {
          const params = { page: this.page + 1 }
          if (this.source) params.source = this.source
          if (this.currentFilial !== 'all' && !this.wholeNetwork) params.filialId = this.currentFilial
          const res = await this.fetchHistory(params)
          this.items = [...this.items, ...res.items]
          this.page = res.meta.page || this.page + 1
          this.hasMore = !!res.meta.hasMore
        } catch (e) {
          this.showErrorFromResponse(e)
        } finally {
          this.loading = false
        }
      },
      toggleBatch (item) {
        const b = this.batches[item.importId]
        if (b) {
          b.open = !b.open
          return
        }
        this.$set(this.batches, item.importId, { open: true, items: [], page: 0, hasMore: false, loading: false })
        this.loadBatch(item.importId)
      },
      async loadBatch (importId) {
        const b = this.batches[importId]
        b.loading = true
        try {
          const res = await this.fetchHistory({ importId, page: b.page + 1 })
          b.items = [...b.items, ...res.items]
          b.page = res.meta.page || b.page + 1
          b.hasMore = !!res.meta.hasMore
        } catch (e) {
          this.showErrorFromResponse(e)
        } finally {
          b.loading = false
        }
      },
      // снимок «до изменения»: у записи зоны или тарифов - снимок её операции,
      // у записи отката - снимок перед откатом (то есть отмена отката)
      versionSnapshotId (item) {
        return item.snapshotId || null
      },
      // кнопка версии - у записей одного филиала; партия импорта охватывает многие,
      // чистка Юпитера тоже - у неё кнопка есть, только когда в истории выбран филиал (бэк даёт его снимок)
      hasVersionButton (item) {
        if (item.event === 'jupiter_cleanup') return !!item.snapshotId
        return item.event !== 'import' && !item.restoredImportId
      },
      canRollbackImport (item) {
        return this.canEdit && item.event === 'import' && item.restorable
      },
      isActive (item) {
        return this.activeSnapshotId != null && this.versionSnapshotId(item) === this.activeSnapshotId
      },
      preview (item) {
        const snapshotId = this.versionSnapshotId(item)
        if (snapshotId) this.$emit('preview-version', { snapshotId, item })
      },
      filialName (id) {
        const f = this.filials.find((x) => x.id === id)
        return f ? [f.address || f.name, f.city].filter(Boolean).join(', ') : '#' + id
      },
      text (item) {
        return describeHistory(item, this.filialName)
      },
      date (s) {
        return historyDate(s)
      },
    },
  }
</script>

<style scoped>
.zh ::v-deep .v-navigation-drawer__content {
  display: flex;
  flex-direction: column;
}
.zh-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px 8px;
}
.zh-head h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 500;
}
.zh-filters {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 0 20px;
}
.zh-toggle {
  border-radius: 8px !important;
}
.zh-toggle ::v-deep .v-btn {
  height: 32px !important;
  font-size: 13px;
  text-transform: none;
  letter-spacing: 0;
  background: #fff !important;
}
.zh-toggle ::v-deep .v-btn.v-btn--active {
  color: #ff9100 !important;
  background: rgba(255, 145, 1, 0.1) !important;
}
.zh-toggle ::v-deep .v-btn.v-btn--active::before {
  opacity: 0 !important;
}
.zh-switch {
  margin: 0;
  padding: 0;
}
.zh-scope {
  padding: 6px 20px 0;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.55);
}
.zh-list {
  flex: 1;
  overflow-y: auto;
  padding: 12px 20px 20px;
}
.zh-item {
  border-bottom: 1px solid #eef0f2;
  padding: 10px 0;
}
.zh-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.55);
}
.zh-user {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.zh-src {
  border-radius: 6px;
  padding: 1px 8px;
  background: #f1f3f6;
}
.zh-src.import {
  background: #fff4e5;
  color: #e65100;
}
.zh-src.restore {
  background: #e3f2fd;
  color: #1565c0;
}
.zh-item.active,
.zh-child.active {
  background: rgba(255, 145, 1, 0.07);
  box-shadow: inset 3px 0 0 #ff9100;
  padding-left: 8px;
}
.zh-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}
.zh-version {
  margin-left: -8px;
  text-transform: none;
  letter-spacing: 0;
  font-size: 12px !important;
}
/* вторая кнопка в строке: без отрицательного отступа первой */
.zh-exit {
  margin-left: 0;
}
.zh-src.jupiter {
  background: #ede7f6;
  color: #5e35b1;
}
.zh-title {
  margin-top: 4px;
  font-size: 14px;
  font-weight: 500;
  word-break: break-word;
}
.zh-title.small {
  font-size: 13px;
}
/* названия зон бывают на три строки - показываем две, целиком в подсказке */
.zh-title,
.zh-line {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.zh-line {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.7);
  word-break: break-word;
}
.zh-filial {
  margin-top: 2px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
}
.zh-more-link {
  margin-top: 4px;
  font-size: 13px;
  color: #ff9100;
  background: none;
  border: 0;
  padding: 0;
  cursor: pointer;
}
.zh-batch {
  margin-top: 6px;
  padding-left: 10px;
  border-left: 2px solid #ffe0b2;
}
.zh-child {
  padding: 4px 0;
}
.zh-empty {
  padding: 24px 0;
  text-align: center;
  color: rgba(0, 0, 0, 0.45);
}
.zh-more {
  margin-top: 12px;
  border-radius: 8px;
  text-transform: none;
  letter-spacing: 0;
  border-color: #e0e3e7;
}
</style>
